import { DocumentTitle } from 'components/DocumentTitle';
import { alterErrorReport, alterErrorReports, useErrorReport } from 'libs/services/s4p';
import { RouteComponentProps } from 'react-router-dom';
import { spacer56 } from '@spotify-internal/encore-web';
import Header from './Header';
import IncorrectRecordings from './IncorectRecordings';
import MissingRecordings from './MissingRecordings';
import styled from 'styled-components';
import { useContext, useEffect, useState } from 'react';
import {
  AlterErrorReportUpdateAction,
  IncorrectRecording,
  MissingRecording,
} from '../../ErrorReporting/types';
import { RecordingsState } from './recordingReducer';
import { BannerContext } from '../../../components/Banner';
import gTagManager from '../../../libs/services/logging';
import { useT } from '@spotify-internal/i18n-core';

// Possibly better to use grid layout TODO later
const SectionMargin = styled.div`
  margin-block-start: ${spacer56};
`;

type Props = RouteComponentProps<{ errorReportId: string }> & {};

const ErrorReportView = ({ match, history }: Props) => {
  const t = useT();
  const errorReportId = match.params.errorReportId;
  const er = useErrorReport(errorReportId);
  const errorReportType =
    er.incorrectRecordings?.length && er.missingRecordings?.length
      ? 'Incorrect and Missing Recordings'
      : er.incorrectRecordings?.length
      ? 'Incorrect Recordings'
      : 'Missing Recordings';
  useEffect(() => {
    gTagManager({
      event: 'openReport',
      songwriterOrgUri: er.songwriterOrgUri,
      songwriterOrgName: er.songwriterName,
      incorrectRecordingCount: er.incorrectRecordings?.length,
      missingWorkCount: er.missingRecordings?.length,
      errorReportType: errorReportType,
      dateReported: er.createdAt,
      reportedBy: er.reporterName,
      status: er.resolved ? 'Resolved' : 'Unresolved',
      message: er.message,
    });
  });
  const [errorReport, setErrorReport] = useState(er);
  const setBanner = useContext(BannerContext);

  function sortRecordingByGid<T extends IncorrectRecording | MissingRecording>(
    recording: T[] | undefined,
  ): T[] | undefined {
    return recording?.sort((a, b) => a.recordingGid.localeCompare(b.recordingGid));
  }

  function withErrorHandling<T>(update: () => Promise<T>) {
    return update().catch(() => {
      setBanner({
        message: t(
          'MSG_ERRORREPORTVIEW_0',
          'Something went wrong. Please refresh the page and try again.',
          'Message that shows when there is an error with an Error Report that requires the user to reload the page',
        ),
        colorSet: 'negative',
        expires: 5000,
      });
    });
  }

  async function onAlterErrorReport(
    operation: AlterErrorReportUpdateAction,
    recordingGids: RecordingsState,
  ): Promise<void> {
    const recordingGidsSet = new Set(
      Object.keys(recordingGids).filter((recordingGid) => recordingGids[recordingGid]),
    );

    await withErrorHandling(async () => {
      const response = await alterErrorReport(errorReportId, recordingGidsSet, operation);
      setErrorReport(response);
    });
  }

  async function onMarkReportResolved(): Promise<void> {
    await withErrorHandling(async () => {
      await alterErrorReports([errorReportId], 'RESOLVE');

      // Unfortunately the data we get back from alterErrorReports is not decorated with recording
      // metadata. We could make another GET fetch for new data but considering the limited scope
      // of the action it's easier if we just manually update the state.
      setErrorReport({
        ...errorReport,
        resolved: true,
        incorrectRecordings: errorReport.incorrectRecordings?.map((recording) => ({
          ...recording,
          resolved: true,
        })),
        missingRecordings: errorReport.missingRecordings?.map((recording) => ({
          ...recording,
          resolved: true,
        })),
      });
    });
  }

  return (
    <DocumentTitle
      title={t(
        'DT_ERRORREPORTVIEW_0',
        '{name} - Error Report - Spotify for Publishing',
        'Title for Error Report page',
        { name: errorReport.songwriterName },
      )}
    >
      <Header
        errorReport={errorReport}
        history={history}
        onMarkReportResolved={onMarkReportResolved}
      />
      <SectionMargin>
        <IncorrectRecordings
          incorrectRecordings={sortRecordingByGid(errorReport.incorrectRecordings)}
          onUpdateIncorrectRecordings={onAlterErrorReport}
          songwriterOrgUri={errorReport.songwriterOrgUri}
          songwriterDisplayName={errorReport.songwriterName}
        />
      </SectionMargin>
      <SectionMargin>
        <MissingRecordings
          missingRecordings={sortRecordingByGid(errorReport.missingRecordings)}
          onUpdateMissingRecordings={onAlterErrorReport}
          songwriterOrgUri={errorReport.songwriterOrgUri}
          songwriterDisplayName={errorReport.songwriterName}
        />
      </SectionMargin>
    </DocumentTitle>
  );
};

export default ErrorReportView;
